import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import ReactSlider from "react-slider";
import "./NewsItem.css";
import RepresentativeCard from "../RepresentativeCard/RepresentativeCard";
import { ReactComponent as TellarepLogoButton } from "../../images/TellarepLogoButton.svg";
import { supabase } from "../../supabaseClient"; // Import your Supabase client setup

const ControlledSlider = ({ value, onChange }) => {
  return (
    <ReactSlider
      value={value}
      onChange={onChange}
      min={0}
      max={2}
      step={1}
      thumbClassName="thumb"
      trackClassName="track"
    />
  );
};

function NewsItem({ article }) {
  const [generatedMessage, setGeneratedMessage] = useState("");
  const [editedMessage, setEditedMessage] = useState("");
  const [isFinalized, setIsFinalized] = useState(false);
  const [viewpoint, setViewpoint] = useState(1); // 0 = Support, 1 = Neutral, 2 = Oppose
  const [loading, setLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showEditedWarning, setShowEditedWarning] = useState(false);
  const [showRepresentatives, setShowRepresentatives] = useState(false);
  const [representatives, setRepresentatives] = useState([]);
  const [error, setError] = useState("");
  const sliderDebounceRef = useRef(null);

  useEffect(() => {
    setEditedMessage(generatedMessage);
  }, [generatedMessage]);

  const handleArticleClick = () => {
    window.open(article.url, "_blank");
  };

  const handleTellARepClick = async (e) => {
    e.stopPropagation();
    const prompt = `Summarize concerns from the article "${article.title}" for a short letter to a representative.`;

    setLoading(true);
    setError("");
    try {
      const response = await axios.post("/.netlify/functions/chatGptHandler", {
        prompt: prompt,
      });
      const message = response.data.message;
      setGeneratedMessage(message);
    } catch (err) {
      console.error("Error generating message:", err);
      const serverError = err.response?.data?.error || err.message;
      setError(`Failed to generate message: ${serverError}`);
    } finally {
      setLoading(false);
    }
  };

  const isMessageComplete = (message) => {
    const lastChar = message.slice(-1);
    return [".", "!", "?"].includes(lastChar);
  };

  const handleSliderChange = async (val) => {
    setViewpoint(val);

    // Clear existing debounce timer
    if (sliderDebounceRef.current) {
      clearTimeout(sliderDebounceRef.current);
    }

    // Set new debounce timer
    sliderDebounceRef.current = setTimeout(async () => {
      const viewpointLabels = ["support", "neutral", "oppose"];
      const selectedViewpoint = viewpointLabels[val];

      const prompt = `Rewrite the following content with a complete ${selectedViewpoint} viewpoint on the issue. Ensure the message is clear and concise. Original content: "${generatedMessage}"`;

      try {
        setLoading(true);
        setError("");
        const response = await axios.post("/.netlify/functions/chatGptHandler", {
          prompt: prompt,
        });
        const newMessage = response.data.message;
        setEditedMessage(newMessage);
        setShowEditedWarning(!isMessageComplete(newMessage));
      } catch (err) {
        console.error("Error adjusting message:", err);
        setError("Failed to adjust message. Please try again.");
      } finally {
        setLoading(false);
      }
    }, 800);
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (sliderDebounceRef.current) {
        clearTimeout(sliderDebounceRef.current);
      }
    };
  }, []);


  const onSendMessage = (type, email) => {
    if (type === "email" && email) {
      const subject = encodeURIComponent(`Re: ${article.title}`);
      const body = encodeURIComponent(editedMessage);
      window.open(`mailto:${email}?subject=${subject}&body=${body}`);
    }
  };

  const handleGenerateXMessage = async () => {
    const xPrompt = `You are an expert Twitter bot. Distill this ${editedMessage}; into a concise message and with no salutations for Twitter.`;

    try {
      const response = await axios.post("/.netlify/functions/chatGptHandler", {
        prompt: xPrompt,
      });

      let xMessage = response.data.message;

      // Truncate message if it exceeds 270 characters (reserving 10 for #tellarep hashtag)
      if (xMessage.length > 270) {
        xMessage = xMessage.substring(0, 267) + "...";
      }

      // Open Twitter with this message (hashtag added via URL param)
      window.open(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(xMessage)}&hashtags=tellarep`
      );
    } catch (error) {
      console.error("Error generating X message:", error);
      alert("Failed to generate Twitter message. Please try again.");
    }
  };

  /*
  const handleShareOnFacebook = () => {
    const appID = "968193114439707"; // replace with your app id
    const href = encodeURIComponent("window.location.href"); // the current page URL or any other link you wish to share
    const redirectURI = encodeURIComponent("http://localhost:8888"); // where to redirect after sharing, typically the same page

    const shareURL = `https://www.facebook.com/dialog/share?app_id=${appID}&display=popup&href=${href}&redirect_uri=${redirectURI}`;
  
    */

  const handleShareOnFacebook = () => {
    const appID = "968193114439707";
    const href = "https://www.google.com";
    const redirectURI = encodeURIComponent("http://localhost:8888");

    const shareURL = `https://www.facebook.com/dialog/share?app_id=${appID}&display=popup&href=${href}&redirect_uri=${redirectURI}`;
    window.open(shareURL, "_blank");
  };

  const handleFinalize = () => {
    setIsFinalized(true);
  };

  const handleEditChange = (e) => {
    setEditedMessage(e.target.value);
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(editedMessage);
  };

  const handleSendEmail = (e) => {
    e.stopPropagation();
    // This is the generic send button - requires rep selection
    // For now, just open a mailto with the message body
    window.open(`mailto:?subject=${encodeURIComponent(article.title)}&body=${encodeURIComponent(editedMessage)}`);
  };

  const handleShareOnX = () => {
    handleGenerateXMessage();
  };

  const handleShareOnWhatsApp = () => {
    window.open(`https://wa.me/?text=${editedMessage}`);
  };

  const getFilteredReps = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (user?.id) {
        const { data: getZipCode, error: getZipCodeError } = await supabase
          .from("users")
          .select()
          .eq("user_id", user.id);

        if (getZipCodeError) {
          console.error("getZipCodeError", getZipCodeError);
          return;
        }

        if (getZipCode?.length) {
          const { data: filteredReps, error: getFilteredRepsError } =
            await supabase
              .from("representatives")
              .select()
              .eq("zip_code", getZipCode[0].zip_code);

          setRepresentatives(filteredReps || []);

          if (getFilteredRepsError) {
            console.error("getFilteredRepsError", getFilteredRepsError);
          }
        } else {
          // User logged in but no ZIP code set — fall back to all reps
          const { data: allReps, error: getAllRepsError } = await supabase
            .from("representatives")
            .select("*");

          setRepresentatives(allReps || []);

          if (getAllRepsError) {
            console.error("getAllRepsError", getAllRepsError);
          }
        }
      } else {
        // Not logged in — show all representatives
        const { data: allReps, error: getAllRepsError } = await supabase
          .from("representatives")
          .select("*");

        setRepresentatives(allReps || []);

        if (getAllRepsError) {
          console.error("getAllRepsError", getAllRepsError);
        }
      }
    } catch (err) {
      console.error("Error fetching representatives:", err);
      setError("Could not load representatives. Please check your profile settings.");
    }
  };

  useEffect(() => {
    if (isFinalized) {
      // Get representatives from database using Netlify function
      // axios
      //   .get("/.netlify/functions/get-reps-from-db")
      //   .then((response) => {
      //     setRepresentatives(response.data);
      //   })
      //   .catch((error) => {
      //     console.error("Error fetching representatives:", error);
      //   });

      // Get representatives from database using supabase directly
      getFilteredReps();
    }
  }, [isFinalized]);

  return (
    <div className="news-item">
      <img
        src={article.urlToImage}
        alt={article.title}
        onClick={handleArticleClick}
      />
      <h3 onClick={handleArticleClick}>{article.title}</h3>
      <p onClick={handleArticleClick}>{article.description}</p>
      <span>{article.source.name}</span>
      <span>{article.timestamp}</span>
      <div className="action-container">
        <TellarepLogoButton
          className="tellarep-button"
          onClick={handleTellARepClick}
        />
        <span className="tellarep-button-text">
          Write to your representative about this!
        </span>
      </div>
      {loading && <p>Loading...</p>}
      {error && <p className="error-message" style={{color: 'red', padding: '10px', backgroundColor: '#ffe6e6', borderRadius: '4px'}}>{error}</p>}
      {generatedMessage && (
        <div>
          <h3>Generated Message:</h3>
          <textarea
            onClick={(e) => e.stopPropagation()}
            value={generatedMessage}
            onChange={(e) => setGeneratedMessage(e.target.value)}
          />
          {!isMessageComplete(generatedMessage) && (
            <p className="warning">
              Warning: This message might be incomplete. Please review and
              adjust as necessary.
            </p>
          )}
        </div>
      )}

      {/* This is where the MessageModal content is integrated */}
      {generatedMessage && !isFinalized && (
        <>
          <h3>Edit Your Draft:</h3>
          <ControlledSlider
            value={viewpoint}
            onChange={handleSliderChange}
          />
          <div className="labels">
            <span>Support</span>
            <span>Neutral</span>
            <span>Oppose</span>
          </div>
          <textarea
            onClick={(e) => e.stopPropagation()}
            value={editedMessage}
            onChange={handleEditChange}
          />
          {showEditedWarning && (
            <p className="warning">
              Warning: This message might be incomplete. Please review and
              adjust as necessary.
            </p>
          )}
          <button
            className="button-style"
            onClick={(e) => {
              e.stopPropagation();
              handleFinalize();
            }}
          >
            Finalize Message
          </button>
        </>
      )}
      {isFinalized && (
        <>
          <h3>Final Message:</h3>
          <textarea value={editedMessage} readOnly />
          <button
            className="button-style"
            onClick={(e) => {
              e.stopPropagation();
              handleCopyToClipboard();
            }}
          >
            Copy to Clipboard
          </button>
          <button className="button-style" onClick={(e) => handleSendEmail(e)}>
            Send Email
          </button>
          <button className="button-style" onClick={handleShareOnX}>
            Share on X (Twitter)
          </button>
          <button className="button-style" onClick={handleShareOnFacebook}>
            Share on Facebook
          </button>
          <button className="button-style" onClick={handleShareOnWhatsApp}>
            Share on WhatsApp
          </button>
        </>
      )}
      <div className="representatives-container">
        {isFinalized &&
          representatives.map((rep) => (
            <RepresentativeCard
              key={rep.id}
              rep={rep}
              message={editedMessage}
              onSendMessage={onSendMessage}
            />
          ))}
      </div>
    </div>
  );
}

export default NewsItem;
